import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {Building2, Plus, Trash2, MapPin, Users, AlertTriangle, RefreshCw, Edit2, Edit3} from 'lucide-react';
import { SedeService } from '../services/apiService';
import { Sede, UserRole } from '../types';
import { useToast } from "../components/ToastContext";
import { useAuth } from '../contexts/AuthContext';

export const SediView: React.FC = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    // Il server restituisce a responsabili e collaboratori solo le loro sedi; creazione,
    // modifica ed eliminazione restano al titolare.
    const { user } = useAuth();
    const isAdmin = user?.role === UserRole.ADMIN;
    const [sedis, setSedis] = useState<Sede[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSedi();
    }, []);

    const loadSedi = async () => {
        setLoading(true);
        try {
            const res = await SedeService.getAll();
            if (res.success) {
                setSedis(res.data);
            } else {
                addToast(res.message || "Errore caricamento dati", "error");
            }
        } catch (error) {
            console.error(error);
            addToast("Impossibile contattare il server", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (sede: Sede) => {
        if (sede.praticheAttive > 0) {
            addToast(`Impossibile eliminare: Ci sono ${sede.praticheAttive} pratiche attive.`, 'error');
            return;
        }
        if ((sede.collaboratoriAttivi || 0) > 0) {
            addToast(`Impossibile eliminare: ${sede.collaboratoriAttivi} collaboratori assegnati. Riassegnali a un'altra sede prima.`, 'error');
            return;
        }

        if (window.confirm(`Eliminare definitivamente la sede "${sede.name}"?`)) {
            const res = await SedeService.delete(sede.id);
            if (res.success) {
                addToast("Sede eliminata con successo", "success");
                loadSedi();
            } else {
                addToast(res.message || "Errore durante l'eliminazione", "error");
            }
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestione Sedi</h1>
                    <p className="text-sm text-gray-500">Gestisci le sedi operative e i relativi responsabili.</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={loadSedi}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Aggiorna lista"
                    >
                        <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
                    </button>
                    {isAdmin && (
                    <button
                        onClick={() => navigate('/sedi/nuova')}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 font-medium shadow-sm transition-colors"
                    >
                        <Plus size={18}/> Nuova Sede
                    </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                    {[1,2,3].map(i => (
                        <div key={i} className="h-48 bg-gray-200 rounded-xl"></div>
                    ))}
                </div>
            ) : sedis.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <Building2 size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Nessuna sede trovata.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sedis.map(sede => (
                        <div key={sede.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow relative group">
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                    <Building2 size={24}/>
                                </div>
                                {isAdmin && (
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => navigate(`/sedi/modifica/${sede.id}`)}
                                        className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                    {(() => {
                                        const collab = sede.collaboratoriAttivi || 0;
                                        const bloccata = sede.praticheAttive > 0 || collab > 0;
                                        const titolo = sede.praticheAttive > 0
                                            ? "Non eliminabile (Pratiche attive)"
                                            : collab > 0
                                                ? `Non eliminabile (${collab} collaboratori assegnati)`
                                                : "Elimina Sede";
                                        return (
                                            <button
                                                onClick={() => handleDelete(sede)}
                                                className={`p-2 rounded-lg transition-colors ${bloccata ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                                                title={titolo}
                                            >
                                                <Trash2 size={18}/>
                                            </button>
                                        );
                                    })()}
                                </div>
                                )}
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-1">{sede.name}</h3>

                            <div className="flex items-center gap-1 text-sm text-gray-500 mb-4 h-5 overflow-hidden text-ellipsis whitespace-nowrap">
                                <MapPin size={14}/> {sede.address || "Indirizzo non specificato"}
                            </div>

                            <div className="space-y-3 pt-4 border-t border-gray-100">
                                {/*
                                  Responsabili della sede: chi la gestisce (può essere più d'uno). Chi vi
                                  lavora solo come collaboratore non compare. Ricalcolati dal BE a ogni GET.
                                */}
                                <div className="flex justify-between text-sm gap-2">
                                    <span className="text-gray-500 flex items-center gap-2 shrink-0">
                                        <Users size={14}/> Responsabil{(sede.responsabili?.length || 0) === 1 ? 'e' : 'i'}
                                    </span>
                                    <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                                        {Array.isArray(sede.responsabili) && sede.responsabili.length > 0 ? (
                                            sede.responsabili.map((r: any) => (
                                                <span key={r.id} className="font-medium text-gray-900 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full text-xs truncate" title={r.name}>
                                                    {r.name}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="font-medium text-gray-400">N/D</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-2">
                                        {sede.praticheAttive > 0 ? <AlertTriangle size={14} className="text-orange-500"/> : <div className="w-3.5"/>}
                                        Pratiche Attive
                                    </span>
                                    <span className={`font-bold ${sede.praticheAttive > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                                        {sede.praticheAttive}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SediView;